/*
 * Atomz typeahead - what the search field shows while you are still typing.
 *
 * The field submitted and nothing else: you typed, you pressed enter, you got
 * a page. Everything the app already knows about the term while you type --
 * the queries other people finish it with, and the products it would return --
 * arrived only after a navigation.
 *
 * This is a dropdown under the field, not the full-screen overlay: popular
 * searches on the left, products with their price and picture on the right,
 * and a footer that says what pressing enter would do.
 *
 * WHERE THE LEFT COLUMN COMES FROM: the Atomz autocomplete answers with
 * products, not with query strings -- what it calls a suggestion is a product
 * title, which is what the right column already shows. So the left column is
 * the merchant's own list, the same one the placeholder typewriter cycles,
 * off data-az-placeholders on the field. No list, no column, and the products
 * take the full width. Nothing here invents a popular search.
 *
 * WHAT IT DOES NOT DO: it does not search. window.AtomzSearch.adapter is the
 * one thing it calls, the same adapter the overlay uses, already pointed at
 * whichever provider the header section is set to and already normalising the
 * response into { products, queries, brands }. A provider change in the theme
 * editor reaches this with nothing to edit here.
 *
 * FAILURE MODE: no adapter, no dropdown, and the field is the plain form it
 * was. A failed or slow request leaves the last good panel rather than
 * blanking it, and a stale response is dropped on its sequence number rather
 * than overwriting a newer one.
 */
(function () {
  'use strict';

  var MIN = 2;          // below this a suggestion list is the whole catalog
  var WAIT = 180;       // a typist's gap between keystrokes, roughly
  var MAX_P = 6;
  var MAX_Q = 6;

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function money(cents) {
    if (cents === null || cents === undefined || cents === '') return '';
    var n = (Number(cents) / 100);
    if (!isFinite(n)) return '';
    return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /*
   * The suggestions the merchant set in the app admin, read out of the page
   * the app already wrote them into rather than typed again in the theme.
   *
   * Appearance > Search Bar > Set Default Search Suggestions renders them as
   * .az-popular-prompt rows inside the app's own search block, and the same
   * list also rides on animate-text[data-text] for the rotating placeholder.
   * Either is read here, so editing that list in the app admin changes the
   * storefront with no theme edit and no deploy.
   *
   * The theme's own list stays as the last resort, for a page the app block is
   * not on and for a shop that has set nothing.
   *
   * Read fresh each time rather than once at boot: the app block renders after
   * this file runs.
   */
  function appList() {
    var rows = document.querySelectorAll('.az-popular-prompt');
    var out = [];
    Array.prototype.forEach.call(rows, function (el) {
      var t = (el.textContent || '').replace(/\s+/g, ' ').trim();
      if (t) out.push(t);
    });
    if (out.length) return out.slice(0, MAX_Q);

    var anim = document.querySelector('animate-text[data-text]');
    if (anim) {
      out = (anim.getAttribute('data-text') || '').split(',')
        .map(function (t) { return t.trim(); })
        .filter(Boolean);
      if (out.length) return out.slice(0, MAX_Q);
    }
    return [];
  }

  function popular(input) {
    var fromApp = appList();
    if (fromApp.length) return fromApp;

    var raw = input.getAttribute('data-az-placeholders');
    if (!raw) return [];
    try {
      var list = JSON.parse(raw);
      return Array.isArray(list) ? list.filter(Boolean).slice(0, MAX_Q) : [];
    } catch (e) { return []; }
  }

  function list(head, queries) {
    var html = '<p class="az-ta__head">' + esc(head) + '</p><ul class="az-ta__list">';
    queries.forEach(function (q) {
      html += '<li><a class="az-ta__q" role="option" href="/search?q=' +
        encodeURIComponent(q) + '">' + esc(q) + '</a></li>';
    });
    return html + '</ul>';
  }

  /* Title takes the slack and the price sits at the end of the row, so a wide
     panel reads as a priced list rather than a title trailing into space. */
  function row(p, fallback) {
    var price = money(p.price);
    return '<li><a class="az-ta__p" role="option" href="' + esc(p.url || fallback || '#') + '">' +
      '<span class="az-ta__thumb">' +
      (p.image ? '<img src="' + esc(p.image) + '" alt="" loading="lazy" width="56" height="56">' : '') +
      '</span><span class="az-ta__meta"><span class="az-ta__title">' + esc(p.title) + '</span></span>' +
      (price ? '<span class="az-ta__price">' + price + '</span>' : '') +
      '</a></li>';
  }

  function products_html(head, products) {
    var html = '<p class="az-ta__head">' + esc(head) + '</p><ul class="az-ta__list">';
    products.forEach(function (p) { html += row(p); });
    return html + '</ul></div>';
  }

  function Typeahead(form) {
    this.form = form;
    this.input = form.querySelector('input[name="q"]');
    if (!this.input) return;

    this.popular = popular(this.input);

    this.panel = document.createElement('div');
    this.panel.className = 'az-ta';
    this.panel.hidden = true;
    this.panel.setAttribute('role', 'listbox');
    form.appendChild(this.panel);

    this.seq = 0;
    this.blocked = false;
    this.timer = null;
    this.items = [];
    this.active = -1;
    this.bind();
  }

  Typeahead.prototype.bind = function () {
    var self = this;

    this.input.setAttribute('autocomplete', 'off');
    this.input.setAttribute('aria-expanded', 'false');

    this.input.addEventListener('input', function () { self.schedule(); });
    this.input.addEventListener('focus', function () {
      self.blocked = false;
      if (self.input.value.trim().length >= MIN) {
        if (self.panel.innerHTML) self.open();
        return;
      }
      self.rest();
    });

    this.input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        /* Chromium clears a type="search" field on Escape, which fires input,
           which empties the term, which is exactly the condition that opens
           the resting panel. So escape shuts the panel and then reopened it.
           Held shut until the shopper does something else. */
        self.blocked = true;
        self.close();
        return;
      }
      if (e.key === 'ArrowDown') { e.preventDefault(); self.move(1); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); self.move(-1); return; }
      if (e.key === 'Enter' && self.active > -1) {
        e.preventDefault();
        self.items[self.active].click();
      }
    });

    /* A click inside the panel is a choice, not a dismissal, and the panel is
       inside the form so a blur to it would close before the click landed. */
    document.addEventListener('click', function (e) {
      if (!self.form.contains(e.target)) self.close();
    });

    this.form.addEventListener('submit', function () { self.close(); });

  };

  /*
   * What the panel holds before a word is typed.
   *
   * Clicking the field used to open nothing: the panel is only built on the
   * second keystroke, so an empty field had nothing to show.
   *
   * The merchant's list and nothing else. Two other things have been in here
   * and come out: products for the top term, which meant a request on every
   * focus and a column of shoes for a query nobody had made, and the shopper's
   * own recent searches. The list is set in the app admin and it is enough on
   * its own.
   */
  Typeahead.prototype.rest = function () {
    if (this.blocked) return;
    /* Held open under the first character too. Closing on keystroke one and
       reopening on keystroke two is a flicker, and one letter is not a change
       of mind about wanting to search. */
    if (this.input.value.trim().length >= MIN) return;

    /* Re-read: the app block renders after this file runs, and the merchant
       can change the list without the page being rebuilt. */
    this.popular = popular(this.input);

    if (!this.popular.length) return;

    var html = '<div class="az-ta__cols"><div class="az-ta__col az-ta__col--q">' +
      list('Suggestions', this.popular) +
      '</div></div>';

    this.panel.innerHTML = html;
    this.items = Array.prototype.slice.call(this.panel.querySelectorAll('[role="option"]'));
    this.active = -1;
    this.open();
  };

  Typeahead.prototype.schedule = function () {
    var self = this;
    clearTimeout(this.timer);
    /* A typed term outranks the resting panel, including one still in flight. */
    this.seq++;
    var term = this.input.value.trim();
    if (term.length >= MIN) this.blocked = false;
    if (term.length < MIN) { this.panel.innerHTML = ''; this.rest(); return; }
    this.timer = setTimeout(function () { self.run(term); }, WAIT);
  };

  Typeahead.prototype.run = function (term) {
    var api = window.AtomzSearch && window.AtomzSearch.adapter;
    if (!api || !api.query) return;

    var self = this;
    var mine = ++this.seq;

    api.query(term).then(
      function (data) {
        /* Dropped rather than drawn: three keystrokes in flight come back in
           whatever order the network decides, and the slowest is the one the
           shopper is least likely to still be looking for. */
        if (mine !== self.seq) return;
        self.render(term, data || {});
      },
      function () { /* leave the last good panel up */ }
    );
  };

  Typeahead.prototype.render = function (term, data) {
    var products = (data.products || []).slice(0, MAX_P);

    /* The provider's own query suggestions where it has them, the merchant's
       list where it does not. Either way the term already typed is not
       offered back. */
    var queries = ((data.queries && data.queries.length) ? data.queries : this.popular)
      .filter(function (q) { return q && q.toLowerCase() !== term.toLowerCase(); })
      .slice(0, MAX_Q);

    if (!products.length && !queries.length) { this.close(); return; }

    var searchUrl = '/search?q=' + encodeURIComponent(term);
    var html = '<div class="az-ta__cols">';

    if (queries.length) {
      html += '<div class="az-ta__col az-ta__col--q"><p class="az-ta__head">' +
        /* One word for both cases. They are suggestions whether the provider
           returned them or the merchant set them in the app admin; "Popular"
           was a claim about traffic that nothing here measures. */
        'Suggestions' +
        '</p><ul class="az-ta__list">';
      queries.forEach(function (q) {
        html += '<li><a class="az-ta__q" role="option" href="/search?q=' +
          encodeURIComponent(q) + '">' + esc(q) + '</a></li>';
      });
      html += '</ul></div>';
    }

    if (products.length) {
      html += '<div class="az-ta__col az-ta__col--p"><p class="az-ta__head">Products</p><ul class="az-ta__list">';
      products.forEach(function (p) { html += row(p, searchUrl); });
      html += '</ul></div>';
    }

    html += '</div><div class="az-ta__foot">' +
      '<a class="az-ta__all" role="option" href="' + searchUrl + '">Search for &ldquo;' +
      esc(term) + '&rdquo;</a>' +
      '<a class="az-ta__all az-ta__all--right" role="option" href="' + searchUrl + '">View all</a>' +
      '</div>';

    this.panel.innerHTML = html;
    this.items = Array.prototype.slice.call(this.panel.querySelectorAll('[role="option"]'));
    this.active = -1;
    this.open();
  };

  Typeahead.prototype.move = function (d) {
    if (!this.items.length) return;
    if (this.active > -1) this.items[this.active].classList.remove('is-active');
    this.active += d;
    if (this.active < 0) this.active = this.items.length - 1;
    if (this.active >= this.items.length) this.active = 0;
    var el = this.items[this.active];
    el.classList.add('is-active');
    if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  };

  Typeahead.prototype.open = function () {
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  };

  Typeahead.prototype.close = function () {
    /* Bumped so a request already in flight cannot reopen what the shopper has
       just dismissed. Escape used to be undone a moment later by the resting
       panel's own fetch arriving and drawing itself. */
    this.seq++;
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    if (this.active > -1 && this.items[this.active]) {
      this.items[this.active].classList.remove('is-active');
    }
    this.active = -1;
  };

  function boot() {
    var form = document.querySelector('.az-searchbar');
    if (!form || form.__azTypeahead) return;
    form.__azTypeahead = new Typeahead(form);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
